/* tslint:disable */

import { MsalConfig } from "./msal-config";
import {
  UserAgentApplication,
  AuthResponse,
  AuthenticationParameters,
  Account,
  Logger,
} from "msal";
import {
  TemporaryCacheKeys,
  ErrorCacheKeys,
  PersistentCacheKeys,
  ServerHashParamKeys,
} from "msal/lib-commonjs/utils/Constants";
import VueRouter from "vue-router";

import { MSALError } from "./msal-error";
import { AuthenticationResult } from "./authentication-result";
import { MsalGuard } from "./msal-guard";
import MsalBroadcast, { InterfaceMsalBroadcast } from "./msal-broadcast";
import { buildConfiguration } from "msal/lib-commonjs/Configuration";

export class AuthenticationContext extends UserAgentApplication {
  public user: any;
  _oauthData = {
    isAuthenticated: false,
    userName: "",
    loginError: "",
    idToken: {},
  };  
  private loginScopes: string[];
  _renewActive!: boolean;

  public broadcastService: InterfaceMsalBroadcast = MsalBroadcast;

  constructor(private _config: MsalConfig, private router: VueRouter) {
    super(buildConfiguration({
      auth: {
        clientId: _config.clientId,
        authority: _config.authority,
        redirectUri: _config.redirectUri,
        validateAuthority: _config.validateAuthority,
        postLogoutRedirectUri: _config.postLogoutRedirectUri,
        navigateToLoginRequestUrl: _config.navigateToLoginRequestUrl,
      },
      cache: {
        cacheLocation: _config.cacheLocation,
        storeAuthStateInCookie: _config.storeAuthStateInCookie,
      },
      framework: {
        isAngular: true,
        unprotectedResources: _config.unprotectedResources,
        protectedResourceMap: new Map(_config.protectedResourceMap),
      },
      system: {
        loadFrameTimeout: _config.loadFrameTimeout,
        logger: new Logger(_config.logger!, {
          correlationId: _config.correlationId,
          level: _config.level,
          piiLoggingEnabled: _config.piiLoggingEnabled,
        }),
      },
    }));

    this.loginScopes = _config.consentScopes!;
    this.updateDataFromCache(this.loginScopes);
    const urlHash = window.location.hash;
    this.processHash(urlHash);

    window.addEventListener("msal:popUpHashChanged", ((e: CustomEvent) => {
      this.getLogger().verbose("popUpHashChanged ");
      this.processHash(e.detail);
    }) as EventListener);

    window.addEventListener("msal:popUpClosed", ((e: any) => {
      const errorParts = e.errorMessage;
      const msalError = new MSALError(e.errorCode, errorParts);
      if (this.loginInProgress()) {
        this.broadcastService.broadcast("msal:loginFailure", msalError);
        this.setloginInProgress(false);
      } else if (this.getAcquireTokenInProgress()) {
        this.broadcastService.broadcast("msal:acquireTokenFailure", msalError);
        this.setAcquireTokenInProgress(false);
      }
    }) as EventListener);

    if (router) {
      const msalGuard = new MsalGuard(_config, this);
      msalGuard.initRouter(router);
    }

    (window as any).__AUTH_CONTEXT__ = this;
  }

  updateDataFromCache(scopes: string[]) {
    // only cache lookup here to not interrupt with events
    const cacheResult: AuthResponse = super.getCachedTokenInternal(
      scopes,
      this.getAccount(),
      ""
    );
    this._oauthData.isAuthenticated =
      cacheResult != null &&
      cacheResult.accessToken !== null &&
      cacheResult.accessToken !== undefined &&
      cacheResult.accessToken.length > 0;
    const user = this.getAccount();
    if (user) {
      this._oauthData.userName = user.name;
      this._oauthData.idToken = user.idToken;
    }
    if (cacheResult && cacheResult.accountState) {
      this._oauthData.loginError =
        cacheResult == null ? "" : cacheResult.accountState;
    }
  }

  private processHash(hash: string) {
    if (this.isCallback(hash)) {
      let isPopup = false;
      let requestInfo = null;
      let callback = null;
      let msal: any;
      // callback can come from popupWindow, iframe or mainWindow
      if (
        window.openedWindows &&
        window.openedWindows.length > 0 &&
        window.openedWindows[window.openedWindows.length - 1].opener &&
        window.openedWindows[window.openedWindows.length - 1].opener.msal
      ) {
        const mainWindow =
          window.openedWindows[window.openedWindows.length - 1].opener;
        msal = mainWindow.msal;
        isPopup = true;
        if(!msal.getRequestInfo) {
          console.log('warn: msal.getRequestInfo undefined');
          return;
        }
        requestInfo = msal.getRequestInfo(hash);
        if (mainWindow.callBackMappedToRenewStates[requestInfo.stateResponse]) {
          callback =
            mainWindow.callBackMappedToRenewStates[requestInfo.stateResponse];
        }
      }
      //redirect flow
      else if (window.parent && window.parent.msal) {
        msal = window.parent.msal;
        if(!msal.getRequestInfo) {
          console.log('warn: msal.getRequestInfo undefined');
          return;
        }
        requestInfo = msal.getRequestInfo(hash);
        if (
          window.parent !== window &&
          (window.parent.callbackMappedToRenewStates as any)[
            requestInfo.stateResponse
          ]
        ) {
          callback = (window.parent.callbackMappedToRenewStates as any)[
            requestInfo.stateResponse
          ];
        } else {
          callback = msal._tokenReceivedCallback;
        }
      }

      this.getLogger().verbose("Processing the hash: " + hash);
      this.saveTokenFromHash(hash, requestInfo);
      // Return to callback if it is sent from iframe
      const token =
        requestInfo.parameters["access_token"] ||
        requestInfo.parameters["id_token"];
      const error = requestInfo.parameters["error"];
      const errorDescription = requestInfo.parameters["error_description"];
      let tokenType = null;
      const msalError = new MSALError(error, errorDescription);
      const authenticationResult = new AuthenticationResult(token);

      if (requestInfo.stateMatch) {
        if (requestInfo.requestType === "RENEW_TOKEN") {
          tokenType = ServerHashParamKeys.ACCESS_TOKEN;
          authenticationResult.tokenType = tokenType;
          this._renewActive = false;
          // Call within the same context without full page redirect keeps the callback
          // id_token or access_token can be renewed
          if (
            window.parent === window &&
            !(window.parent.callbackMappedToRenewStates as any)[
              requestInfo.stateResponse
            ]
          ) {
            if (token) {
              this.broadcastService.broadcast(
                "msal:acquireTokenSuccess",
                authenticationResult
              );
            } else if (error && errorDescription) {
              this.broadcastService.broadcast(
                "msal:acquireTokenFailure",
                msalError
              );
            }
          }
        } else if (requestInfo.requestType === "LOGIN") {
          tokenType = PersistentCacheKeys.IDTOKEN;
          authenticationResult.tokenType = tokenType;
          this.updateDataFromCache(this.loginScopes);
          if (this._oauthData.userName) {
            setTimeout(() => {
              // id_token is added as token for the app
              this.updateDataFromCache(this.loginScopes);
            }, 1);
            this.broadcastService.broadcast(
              "msal:loginSuccess",
              authenticationResult
            );
          } else {
            this.broadcastService.broadcast("msal:loginFailure", msalError);
          }
        }

        if (callback && typeof callback === "function") {
          callback(errorDescription, token, error, tokenType);
        }

        // since this is a token renewal request in iFrame, we don't need to proceed with the location change.
        if (window.parent !== window) {
          //in iframe
          if (event && event.preventDefault) {
            event.preventDefault();
          }
          return;
        }

        // redirect to login start page
        if (window.parent === window && !isPopup) {
          if (this._config.navigateToLoginRequestUrl) {
            const loginStartPage = this.cacheStorage.getItem(
              TemporaryCacheKeys.LOGIN_REQUEST
            );
            this.cacheStorage.setItem(TemporaryCacheKeys.URL_HASH, hash);
            if (
              typeof loginStartPage !== "undefined" &&
              loginStartPage &&
              loginStartPage.length !== 0
            ) {
              // prevent the current location change and redirect the user back to the login start page
              this.getLogger().verbose(
                "Redirecting to start page: " + loginStartPage
              );
              window.location.href = loginStartPage;
            }
          }
          //redirect to redirect uri. No page reload here since we are only removing the url after the hash
          else {
            window.location.hash = "";
          }
        }
      } else {
        // state did not match, broadcast an error
        this.broadcastService.broadcast("msal:stateMismatch", msalError);
      }
    } else {
      const pendingCallback = this.cacheStorage.getItem(
        TemporaryCacheKeys.URL_HASH
      );
      if (pendingCallback) {
        this.processRedirectCallBack(pendingCallback);
      }
    }
  }

  private processRedirectCallBack(hash: string): void {
    this.getLogger().info("Processing the callback from redirect response");
    let msal: any;
    if(!msal.getRequestInfo) {
      console.log('warn: msal.getRequestInfo undefined');
      return;
    }
    const requestInfo = msal.getRequestInfo(hash);
    const token =
      (requestInfo.parameters as any)[ServerHashParamKeys.ACCESS_TOKEN] ||
      (requestInfo.parameters as any)[PersistentCacheKeys.IDTOKEN];
    const errorDesc = (requestInfo.parameters as any)[
      ErrorCacheKeys.ERROR_DESC
    ];
    const error = (requestInfo.parameters as any)[ErrorCacheKeys.ERROR];
    let tokenType: string;
    this.cacheStorage.removeItem(TemporaryCacheKeys.URL_HASH);
    const msalError = new MSALError(error, errorDesc);
    const authenticationResult = new AuthenticationResult(token);
    if ((requestInfo.parameters as any)[ServerHashParamKeys.ACCESS_TOKEN]) {
      tokenType = ServerHashParamKeys.ACCESS_TOKEN;
      if (token) {
        authenticationResult.tokenType = tokenType;
        this.broadcastService.broadcast(
          "msal:acquireTokenSuccess",
          authenticationResult
        );
      } else if (error && errorDesc) {
        this.broadcastService.broadcast("msal:acquireTokenFailure", msalError);
      }
    } else {
      tokenType = PersistentCacheKeys.IDTOKEN;
      if (token) {
        authenticationResult.tokenType = tokenType;
        this.broadcastService.broadcast(
          "msal:loginSuccess",
          authenticationResult
        );
      } else if (error && errorDesc) {
        this.broadcastService.broadcast("msal:loginFailure", msalError);
      }
    }
  }

  protected clearCache() {
    super.clearCache();
  }

  getLogger() {
    return super.getLogger();
  }

  getCacheStorage(): any {
    return this.cacheStorage;
  }

  public isCallback(hash: string): boolean {
    return super.isCallback(hash);
  }

  public loginRedirect(consentScopes?: AuthenticationParameters) {
    this.getLogger().verbose("login redirect flow");
    super.loginRedirect(consentScopes);
  }

  public loginPopup(consentScopes?: AuthenticationParameters): Promise<any> {
    this.getLogger().verbose("login popup flow");
    return new Promise((resolve, reject) => {
      super.loginPopup(consentScopes!).then(
        (idToken: any) => {
          const authenticationResult = new AuthenticationResult(
            idToken,
            "idToken"
          );
          this.broadcastService.broadcast(
            "msal:loginSuccess",
            authenticationResult
          );
          resolve(idToken);
        },
        (error: any) => {
          const errorParts = error.errorMessage;
          const msalError = new MSALError(error.errorCode, errorParts);
          this.getLogger().error("Error during login:\n" + error);
          this.broadcastService.broadcast("msal:loginFailure", msalError);
          reject(error);
        }
      );
    });
  }

  public logout(): void {
    this.user = null;
    super.logout();
  }

  getCachedTokenInternal(
    scopes: string[],
    account: Account,
    state: string
  ): AuthResponse {
    return super.getCachedTokenInternal(scopes, account, state);
  }

  public acquireTokenSilent(
    scopes: AuthenticationParameters
  ): Promise<AuthResponse> {
    return new Promise((resolve, reject) => {
      super.acquireTokenSilent(scopes).then(
        (token: any) => {
          this._renewActive = false;
          const authenticationResult = new AuthenticationResult(token);
          this.broadcastService.broadcast(
            "msal:acquireTokenSuccess",
            authenticationResult
          );
          resolve(token);
        },
        (error: any) => {
          const errorParts = error.errorMessage;
          const msalError = new MSALError(error.errorCode, errorParts);
          this._renewActive = false;
          this.broadcastService.broadcast(
            "msal:acquireTokenFailure",
            msalError
          );
          this.getLogger().error(
            "Error when acquiring token for scopes: " + scopes + " " + error
          );
          reject(error);
        }
      );
    });
  }

  public acquireTokenPopup(
    scopes: AuthenticationParameters
  ): Promise<AuthResponse> {
    return new Promise((resolve, reject) => {
      super.acquireTokenPopup(scopes).then(
        (token: any) => {
          this._renewActive = false;
          const authenticationResult = new AuthenticationResult(token);
          this.broadcastService.broadcast(
            "msal:acquireTokenSuccess",
            authenticationResult
          );
          resolve(token);
        },
        (error: any) => {
          const errorParts = error.errorMessage;
          const msalError = new MSALError(error.errorCode, errorParts);
          this._renewActive = false;
          this.broadcastService.broadcast(
            "msal:acquireTokenFailure",
            msalError
          );
          this.getLogger().error(
            "Error when acquiring token for scopes : " + scopes + " " + error
          );
          reject(error);
        }
      );
    });
  }

  public acquireTokenRedirect(scopes: AuthenticationParameters) {
    const acquireTokenStartPage = this.cacheStorage.getItem(
      TemporaryCacheKeys.LOGIN_REQUEST
    );
    if (window.location.href !== acquireTokenStartPage)
      this.cacheStorage.setItem(
        TemporaryCacheKeys.LOGIN_REQUEST,
        window.location.href
      );
    super.acquireTokenRedirect(scopes);
  }

  public loginInProgress(): boolean {
    return super.getLoginInProgress();
  }

  public getAccount(): Account {
    return super.getAccount();
  }

  getScopesForEndpoint(endpoint: string) {
    return super.getScopesForEndpoint(endpoint);
  }

  clearCacheForScope(accessToken: string) {
    super.clearCacheForScope(accessToken);
  }

  info(message: string) {
    this.getLogger().info(message);
  }

  verbose(message: string) {
    this.getLogger().verbose(message);
  }

  removeItem(key: string) {
    this.cacheStorage.removeItem(key);
  }
}
